<template>
  <div class="home-root">
    <header class="page-brand" aria-hidden="true">
      <img class="brandLogo" :src="brandLogoSrc" alt="ePRO易宝" />
    </header>

    <div class="shell">
      <section class="visual" aria-hidden="true">
        <img class="visual-img" :src="illustrationSrc" alt="" />
      </section>

      <section class="panel">
        <div class="panel-card">
          <header class="brand">
            <div class="brandTitle">AI视频面试平台</div>
          </header>

          <form novalidate @submit.prevent="onSubmit">
            <label style="margin-bottom: 12px;">
              <input
                v-model.trim="account"
                class="text-input input-account"
                type="text"
                placeholder="请输入工号"
                autocomplete="username"
                @input="validate"
              />
            </label>

            <label style="margin-bottom: 12px;">
              <div class="password-input-wrap">
                <input
                  v-model.trim="password"
                  class="text-input input-password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="请输入密码"
                  autocomplete="current-password"
                  @input="validate"
                />
                <button
                  type="button"
                  class="password-toggle"
                  :aria-label="showPassword ? '隐藏密码' : '显示密码'"
                  :aria-pressed="showPassword ? 'true' : 'false'"
                  :title="showPassword ? '隐藏密码' : '显示密码'"
                  @click="togglePasswordVisibility"
                >
                  <i
                    class="el-icon-view"
                    :class="{ 'is-visible': showPassword, 'is-hidden': !showPassword }"
                    aria-hidden="true"
                  ></i>
                </button>
              </div>
            </label>

            <label class="captcha-label">
              <div class="captcha-row">
                <input
                  v-model.trim="captcha"
                  class="text-input input-captcha"
                  type="text"
                  placeholder="点击图片刷新"
                  maxlength="6"
                  autocomplete="off"
                  @input="validate"
                  style="margin-right: 8px;"
                />
                <el-button
                  size="small"
                  native-type="button"
                  class="captcha-img"
                  title="点击刷新验证码"
                  @click="refreshCaptcha"
                >
                  {{ currentCaptcha }}
                </el-button>
              </div>
            </label>

            <!-- <div class="row">
              <span>
                <input id="remember" v-model="remember" type="checkbox" />
                <label for="remember" class="row-label">记住密码</label>
              </span>
              <a href="javascript:void 0" @click.prevent="onForget">忘记密码</a>
            </div> -->

            <div class="error" role="alert" aria-live="polite">{{ error }}</div>

            <el-button
              size="small"
              native-type="submit"
              class="submit"
              :disabled="!canSubmit || loading"
            >
              {{ loading ? "登录中..." : "登录" }}
            </el-button>
          </form>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import illustrationSrc from "@/assets/home-illustration.png";
import brandLogoSrc from "@/assets/brand-logo.png";
import { login } from "@/api/modules/auth";
import { setToken } from "@/utils/token";
import md5 from "@/utils/md5";
import { fetchUserPrivilege } from "@/api/modules/privilege";

const CAPTCHA_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

function generateCaptcha() {
  let code = "";
  for (let i = 0; i < 4; i++) {
    code += CAPTCHA_CHARS[Math.floor(Math.random() * CAPTCHA_CHARS.length)];
  }
  return code;
}

export default {
  name: "AppHome",
  data() {
    const initialCaptcha = generateCaptcha();
    return {
      illustrationSrc,
      brandLogoSrc,
      account: "",
      password: "",
      captcha: "",
      remember: false,
      showPassword: false,
      error: "",
      canSubmit: false,
      currentCaptcha: initialCaptcha,
      loading: false,
    };
  },
  methods: {
    resolveLoginPayload(res) {
      // 兼容常见返回结构：res / res.data / res.response
      const source = res && (res.data || res.response || res.res || res);
      return source || {};
    },
    async loadPrivilege(userId) {
      if (!userId) return;
      try {
        const res = await fetchUserPrivilege(userId);
        const root = res && (res.data || res.response || res.res || res);
        const items = Array.isArray(root && root.data)
          ? root.data
          : Array.isArray(root)
          ? root
          : [];

        const menuPaths = [];
        const buttonCodes = [];
        const menuButtonCodesByPath = {};

        const walk = (nodes = [], currentMenuPath = null) => {
          nodes.forEach((n) => {
            if (n.menu_type === "MENU") {
              const nextMenuPath = n.path || currentMenuPath;
              if (n.path) menuPaths.push(n.path);
              if (n.children && n.children.length) {
                walk(n.children, nextMenuPath);
              }
              return;
            }

            if (n.menu_type === "BUTTON") {
              if (n.code) {
                buttonCodes.push(n.code);
                if (currentMenuPath) {
                  if (!menuButtonCodesByPath[currentMenuPath]) menuButtonCodesByPath[currentMenuPath] = [];
                  menuButtonCodesByPath[currentMenuPath].push(n.code);
                }
              }
              if (n.children && n.children.length) {
                walk(n.children, currentMenuPath);
              }
              return;
            }

            if (n.children && n.children.length) {
              walk(n.children, currentMenuPath);
            }
          });
        };

        walk(items);

        // 去重，避免后端重复下发导致 UI 抖动
        const uniq = (arr) => Array.from(new Set(arr || []));
        const dedupedMenuPaths = uniq(menuPaths);
        const dedupedButtonCodes = uniq(buttonCodes);
        const dedupedMenuButtonCodesByPath = {};
        Object.keys(menuButtonCodesByPath).forEach((k) => {
          dedupedMenuButtonCodesByPath[k] = uniq(menuButtonCodesByPath[k]);
        });

        if (this.$store && typeof this.$store.commit === "function") {
          this.$store.commit("setPrivilege", {
            menuPaths: dedupedMenuPaths,
            buttonCodes: dedupedButtonCodes,
            menuButtonCodesByPath: dedupedMenuButtonCodesByPath,
          });
        }

        try {
          sessionStorage.setItem("privilege_menu_paths", JSON.stringify(dedupedMenuPaths));
          sessionStorage.setItem("privilege_button_codes", JSON.stringify(dedupedButtonCodes));
          sessionStorage.setItem(
            "privilege_menu_button_codes_by_path",
            JSON.stringify(dedupedMenuButtonCodesByPath)
          );
        } catch (e) {
          // ignore
        }
      } catch (e) {
        // ignore 权限加载失败不阻塞登录
      }
    },
    refreshCaptcha() {
      this.currentCaptcha = generateCaptcha();
      this.captcha = "";
      this.validate();
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    validate() {
      const acc = this.account.trim();
      const pwd = this.password.trim();
      const cap = this.captcha.trim();

      if (!acc || !pwd || !cap) {
        this.error = "";
        this.canSubmit = false;
        return;
      }
      if (pwd.length < 6) {
        this.error = "密码至少 6 位。";
        this.canSubmit = false;
        return;
      }
      if (cap.length !== 4) {
        this.error = "验证码应为4位";
        this.canSubmit = false;
        return;
      }
      if (cap.toUpperCase() !== this.currentCaptcha) {
        this.error = "验证码错误，请重新输入或点击刷新";
        this.canSubmit = false;
        return;
      }
      this.error = "";
      this.canSubmit = true;
    },
    onForget() {
      if (this.$alert) {
        this.$alert(
          "上线版本会通过企业邮箱/统一身份认证重置密码。",
          "忘记密码（原型）",
          { confirmButtonText: "知道了" }
        );
      } else {
        window.alert(
          "忘记密码（原型）：\n\n上线版本会通过企业邮箱/统一身份认证重置密码。"
        );
      }
    },
    async onSubmit() {
      this.validate();
      if (!this.canSubmit || this.loading) return;
      this.loading = true;
      this.error = "";
      try {
        const resp = await login({
          username: this.account.trim(),
          // 后端要求：密码 md5 加密
          password: md5(this.password.trim()),
        });
        const payload = this.resolveLoginPayload(resp);
        const accessToken = payload.access_token || payload.accessToken || payload.token || "";
        const tokenType = payload.token_type || payload.tokenType || "Bearer";
        const userInfo = payload.user_info || payload.userInfo || null;
        if (!accessToken) {
          this.error = payload.msg || payload.message || "登录失败：未获取到登录凭证";
          return;
        }
        setToken({ accessToken, tokenType });

        // 登录信息仅 sessionStorage，按页签隔离
        try {
          if (userInfo) {
            sessionStorage.setItem("user_info", JSON.stringify(userInfo));
          }
        } catch (e) {
          // ignore
        }

        // 写入 store（每次登录都覆盖更新）
        if (this.$store && typeof this.$store.commit === "function") {
          this.$store.commit("setAuth", { accessToken, tokenType, userInfo });
        }

        try {
          sessionStorage.setItem("login_user", this.account.trim());
        } catch (e) {
          // ignore
        }

        const userId = (userInfo && (userInfo.userid || userInfo.user_id)) || "";
        if (userId) {
          await this.loadPrivilege(userId);
        }
        this.$router.push({
          path: "/interviewer/dashboard",
          query: { user: (userInfo && userInfo.work_no) || this.account.trim(), captchaOk: "1" },
        });
      } catch (e) {
        const msg = e && e.data && (e.data.msg || e.data.message);
        this.error = msg || "账号或密码错误，请重试";
        this.refreshCaptcha();
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.validate();
  },
};
</script>

<style scoped>
.home-root {
  --bg: #f1f4ff;
  --panel-bg: #ffffff;
  --text: #0f172a;
  --muted: #64748b;
  --line: rgba(15, 23, 42, 0.06);
  --brand: #3b6cff;
  --shadow: none;
  --shadow-soft: none;
  --radius: 18px;
  --radius-sm: 8px;
  --focus: 0 0 0 4px rgba(59, 108, 255, 0.16);

  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px 14px;
  background: var(--bg);
  color: var(--text);
}

.page-brand {
  position: fixed;
  top: 20px;
  left: 32px;
  z-index: 10;
}

.shell {
  width: 100%;
  max-width: 980px;
  min-height: 520px;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1.35fr 1fr;
}

.visual {
  position: relative;
  padding: 28px 28px;
  background: var(--bg);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}



.visual-img {
  position: relative;
  width: min(520px, 92%);
  height: auto;
  max-height: 440px;
  object-fit: contain;
  filter: none;
}

.panel {
  padding: 28px 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.panel-card {
  width: 100%;
  max-width: 420px;
  border-radius: var(--radius);
  background: var(--panel-bg);
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: none;
  padding: 28px 26px 24px;
  display: flex;
  flex-direction: column;
}

.brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}

.brandLogo {
  height: 32px;
  width: auto;
  display: block;
}

.brandTitle {
  font-weight: 900;
  letter-spacing: 0.5px;
  font-size: 22px;
}

form {
  display: flex;
  flex-direction: column;
  gap: 0px;
  margin-top: 8px;
}

label {
  font-size: 13px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

label span:first-child {
  font-weight: 800;
  color: #0f172a;
}

.text-input {
  border-radius: var(--radius-sm);
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #ffffff;
  color: var(--text);
  padding: 10px 12px;
  font-size: 14px;
  outline: none;
  transition: 0.12s ease;
}

input[type="text"],
input[type="password"] {
  border-radius: var(--radius-sm);
}

input::placeholder {
  color: rgba(100, 116, 139, 0.65);
}

input:focus {
  border-color: var(--brand);
  box-shadow: var(--focus);
}

.captcha-label {
  gap: 2px;
}

.captcha-row {
  display: flex;
  gap: 0px;
  align-items: center;
  margin-top: 2px;
}

.captcha-row input {
  flex: 1;
  min-width: 0;
}

.password-input-wrap {
  position: relative;
}

.password-input-wrap .input-password {
  width: 100%;
  padding-right: 42px;
}

.password-toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  padding: 2px;
  line-height: 1;
}

.password-toggle:hover {
  color: #3b6cff;
}

.password-toggle:focus {
  outline: none;
}

.password-toggle i {
  position: relative;
  display: inline-block;
  font-size: 16px;
}

.password-toggle i.is-visible {
  color: #3b6cff;
}

.password-toggle i.is-hidden::after {
  content: "";
  position: absolute;
  left: 1px;
  top: 50%;
  width: 14px;
  height: 2px;
  background: currentColor;
  border-radius: 1px;
  transform: translateY(-50%) rotate(-35deg);
}

.input-account {
  background:
    url("~@/assets/icon-account.svg") no-repeat 10px center,
    #ffffff;
  background-size: 18px 18px, auto;
  padding-left: 36px;
}

.input-password {
  background:
    url("~@/assets/icon-password.svg") no-repeat 10px center,
    #ffffff;
  background-size: 18px 18px, auto;
  padding-left: 36px;
}

.input-captcha {
  background:
    url("~@/assets/icon-captcha.svg") no-repeat 10px center,
    #ffffff;
  background-size: 18px 18px, auto;
  padding-left: 36px;
}

.captcha-img {
  height: 38px;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Courier New", monospace;
  font-weight: 800;
  font-size: 20px;
  letter-spacing: 4px;
  color: #1e293b;
  border: 1px solid rgba(15, 23, 42, 0.1);
  cursor: pointer;
  user-select: none;
  transition: 0.1s ease;
  box-shadow: inset 0 1px 3px rgba(15, 23, 42, 0.04);
  padding: 0 14px;
  background: linear-gradient(145deg, #f8fbff, #eef3ff);
  text-shadow: 1px 1px 0 rgba(255, 255, 255, 0.7);
}

.captcha-img:hover {
  background: linear-gradient(145deg, #f2f7ff, #e5eeff);
  border-color: rgba(59, 108, 255, 0.35);
}

.captcha-img:active {
  transform: scale(0.98);
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: var(--muted);
  margin-top: 2px;
}

.row span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.row input[type="checkbox"] {
  margin: 0;
}

.row-label {
  cursor: pointer;
}

.submit {
  border-radius: 12px;
  border: 1px solid var(--brand);
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  background: linear-gradient(135deg, #3b6cff, #4f86ff);
  color: #fff;
  box-shadow: none;
  transition: 0.12s ease;
  margin-top: 12px;
  margin-bottom: 6px;
}

.submit:hover {
  background: linear-gradient(135deg, #4f86ff, #3b6cff);
}

.submit:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.error {
  font-size: 12px;
  color: #ef4444;
  min-height: 16px;
}

a {
  color: inherit;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

@media (max-width: 920px) {
  .shell {
    grid-template-columns: 1fr;
    max-width: 560px;
  }

  .visual {
    min-height: 280px;
    padding: 22px 20px;
  }

  .panel {
    padding: 22px 20px;
  }
}

@media (max-width: 768px) {
  .text-input {
    font-size: 16px;
    min-height: 40px;
    box-sizing: border-box;
  }

  .submit {
    min-height: 42px;
    padding: 10px 12px;
    font-size: 15px;
  }
}

@media (max-width: 560px) {
  .home-root {
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding: 14px 12px 18px;
    width: 100%;
    min-height: 100vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* 小屏避免 fixed logo 挡住表单 */
  .page-brand {
    position: static;
    margin: 6px 0 12px;
    display: flex;
    justify-content: flex-start;
    width: 100%;
  }

  .brandLogo {
    height: 22px;
  }

  .shell {
    width: 100%;
    max-width: 420px;
    min-height: 0;
    border-radius: 16px;
    overflow: visible;
    background: transparent;
  }

  .visual {
    min-height: 200px;
    padding: 16px 14px;
  }

  .visual-img {
    width: min(420px, 96%);
    max-height: 220px;
  }

  .panel {
    padding: 0;
  }

  .panel-card {
    max-width: 100%;
    padding: 18px 16px 16px;
    border-radius: 16px;
    border-left: none;
    border-right: none;
    box-sizing: border-box;
  }

  .brandTitle {
    font-size: 18px;
  }

  .text-input {
    font-size: 16px;
    padding: 10px 12px;
  }

  /* 小屏：图标与占位符避免重叠（加大左侧内边距并右移图标） */
  .input-account,
  .input-password,
  .input-captcha {
    background-position: 12px center, 0 0;
    padding-left: 44px;
    background-size: 18px 18px, auto;
  }

  .captcha-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
    align-items: center;
  }

  .captcha-row .input-captcha {
    flex: 1;
    min-width: 0;
  }

  .captcha-img {
    width: 112px;
    min-width: 112px;
    justify-content: center;
    letter-spacing: 4px;
    margin-top: 0;
    padding: 0 8px;
    flex: 0 0 auto;
  }

  .submit {
    width: 100%;
  }
}

@media (max-width: 360px) {
  .visual {
    display: none;
  }

  .panel {
    padding: 0;
  }

  .panel-card {
    padding: 16px 14px 14px;
  }

  .brandTitle {
    font-size: 17px;
  }

  .captcha-img {
    width: 96px;
    min-width: 96px;
    font-size: 16px;
    letter-spacing: 2px;
    padding: 0 6px;
  }
}
</style>
